<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import { useStore } from 'dashboard/composables/store';
import dealReportsAPI from 'dashboard/api/dealReports';
import { vOnClickOutside } from '@vueuse/components';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useGoalsProgress } from 'dashboard/composables/useGoalsProgress';
import ExportPdfButton from '../components/ExportPdfButton.vue';
import { commercialLegend, pdfNotes as buildNotes } from '../exportLegends';
import GoalsHero from '../components/Comercial/GoalsHero.vue';
import SummaryCards from '../components/Comercial/SummaryCards.vue';
import FunnelSection from '../components/Comercial/FunnelSection.vue';
import EvolutionSection from '../components/Comercial/EvolutionSection.vue';
import SalesRepSection from '../components/Comercial/SalesRepSection.vue';
import SourceSection from '../components/Comercial/SourceSection.vue';
import LossReasonSection from '../components/Comercial/LossReasonSection.vue';
import StalledSection from '../components/Comercial/StalledSection.vue';
import WidgetsSection from '../components/Comercial/Widgets/WidgetsSection.vue';
import CommercialFilter from '../components/Comercial/CommercialFilter.vue';
import ActiveFilterPreview from 'dashboard/components-next/filter/ActiveFilterPreview.vue';
import Button from 'next/button/Button.vue';
import {
  filtersToParams,
  useCommercialFilterTypes,
} from '../components/Comercial/commercialFilterTypes';

const { t } = useI18n();
const pageRef = ref(null);
const store = useStore();
const pipelinesStore = usePipelinesStore();
const isWidgetsEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    store.getters.getCurrentAccountId,
    'commercial_widgets'
  )
);
const { filterTypes } = useCommercialFilterTypes();
useGoalsProgress();

const PERIODS = [
  { id: 'today', days: 0 },
  { id: 'last7days', days: 6 },
  { id: 'last30days', days: 29 },
  { id: 'last90days', days: 89 },
  { id: 'custom', days: null },
];

const selectedPeriod = ref('last30days');
// 'movement': ganhas/perdidas pela data de fechamento; 'entry': só o que entrou no período.
const DATE_MODES = ['movement', 'entry'];
const dateMode = ref('movement');
const customFrom = ref('');
const customUntil = ref('');
const appliedFilters = ref([]);
const isFilterOpen = ref(false);
const utmValues = ref({});

const applyFilters = filters => {
  appliedFilters.value = filters;
  isFilterOpen.value = false;
};

// Etiquetas dos filtros aplicados, com o nome do campo traduzido.
const previewFilters = computed(() =>
  appliedFilters.value.map(filter => ({
    ...filter,
    attributeKey: filterTypes.value.find(
      type => type.attributeKey === filter.attributeKey
    ).label,
  }))
);

const filterOutsideClickHandler = [
  () => {
    isFilterOpen.value = false;
  },
  { ignore: ['#toggleCommercialFilterButton'] },
];

const dateRange = computed(() => {
  if (selectedPeriod.value === 'custom') {
    return {
      since: customFrom.value ? startOfDay(new Date(customFrom.value)) : null,
      until: customUntil.value ? endOfDay(new Date(customUntil.value)) : null,
    };
  }

  const period = PERIODS.find(item => item.id === selectedPeriod.value);
  const today = new Date();
  return {
    since: startOfDay(subDays(today, period?.days ?? 29)),
    until: endOfDay(today),
  };
});

const formattedRange = computed(() => {
  if (!dateRange.value.since || !dateRange.value.until) return '';
  return `${format(dateRange.value.since, 'dd/MM/yyyy')} – ${format(dateRange.value.until, 'dd/MM/yyyy')}`;
});

const hasCompleteRange = computed(
  () => !!dateRange.value.since && !!dateRange.value.until
);

const pdfLegend = computed(() => commercialLegend(t));
const pdfNotes = computed(() =>
  buildNotes(t, ['SNAPSHOT', 'CURRENCY', 'FILTER_LOGIC'])
);

const dateModeLabel = mode =>
  t(`DASHBOARD.COMERCIAL.DATE_MODE.${mode.toUpperCase()}`);

const periodLabel = computed(() => {
  const mode = t('DASHBOARD.COMERCIAL.DATE_MODE.PDF', {
    mode: dateModeLabel(dateMode.value).toLowerCase(),
  });
  if (selectedPeriod.value === 'custom') {
    return `${formattedRange.value} · ${mode}`;
  }
  const name = t(
    `DASHBOARD.ATENDIMENTO.PERIOD.${selectedPeriod.value.toUpperCase()}`
  );
  return `${name} (${formattedRange.value}) · ${mode}`;
});

const filterLines = computed(() =>
  previewFilters.value.map(
    filter =>
      `${filter.attributeKey}: ${filter.values.map(value => value.name).join(', ')}`
  )
);

const baseParams = computed(() => {
  if (!hasCompleteRange.value) return null;

  return {
    since: Math.floor(dateRange.value.since.getTime() / 1000),
    until: Math.floor(dateRange.value.until.getTime() / 1000),
    date_mode: dateMode.value,
    ...filtersToParams(appliedFilters.value),
  };
});

onMounted(async () => {
  pipelinesStore.get();
  store.dispatch('agents/get');
  store.dispatch('attributes/get');
  utmValues.value = (await dealReportsAPI.getUtmValues()).data;
});
</script>

<template>
  <div
    ref="pageRef"
    class="flex flex-col w-full h-full gap-4 p-6 overflow-y-auto"
  >
    <div data-pdf-ignore class="flex items-center justify-between gap-2">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('DASHBOARD.COMERCIAL.TITLE') }}
      </h1>
      <ExportPdfButton
        :target="pageRef"
        :page-title="t('DASHBOARD.COMERCIAL.TITLE')"
        slug="comercial"
        :period-label="periodLabel"
        :filter-lines="filterLines"
        :notes="pdfNotes"
        :legend="pdfLegend"
        :disabled="!hasCompleteRange"
      />
    </div>

    <div
      data-pdf-ignore
      class="flex flex-col gap-3 p-3 border rounded-lg lg:flex-row lg:items-center border-n-weak"
    >
      <div class="flex flex-wrap gap-1">
        <button
          v-for="period in PERIODS"
          :key="period.id"
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="
            selectedPeriod === period.id
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="selectedPeriod = period.id"
        >
          {{ t(`DASHBOARD.ATENDIMENTO.PERIOD.${period.id.toUpperCase()}`) }}
        </button>
      </div>

      <div v-if="selectedPeriod === 'custom'" class="flex items-center gap-2">
        <input
          v-model="customFrom"
          type="date"
          class="!h-8 !py-0 !mb-0 !text-sm rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
        <span class="text-n-slate-10">{{
          t('DASHBOARD.ATENDIMENTO.PERIOD.CUSTOM_UNTIL')
        }}</span>
        <input
          v-model="customUntil"
          type="date"
          class="!h-8 !py-0 !mb-0 !text-sm rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
      </div>

      <span v-if="formattedRange" class="text-xs text-n-slate-10">{{
        formattedRange
      }}</span>

      <div class="flex items-center gap-1">
        <span class="text-xs text-n-slate-10">{{
          t('DASHBOARD.COMERCIAL.DATE_MODE.LABEL')
        }}</span>
        <button
          v-for="mode in DATE_MODES"
          :key="mode"
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="
            dateMode === mode
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="dateMode = mode"
        >
          {{ dateModeLabel(mode) }}
        </button>
      </div>

      <div class="relative lg:ltr:ml-auto lg:rtl:mr-auto">
        <Button
          id="toggleCommercialFilterButton"
          sm
          faded
          slate
          icon="i-lucide-list-filter"
          :label="t('DASHBOARD.COMERCIAL.FILTER.BUTTON')"
          @click="isFilterOpen = !isFilterOpen"
        />
        <span
          v-if="appliedFilters.length"
          class="absolute flex items-center justify-center w-4 h-4 text-xs text-white rounded-full -top-1.5 ltr:-right-1.5 rtl:-left-1.5 bg-n-brand"
        >
          {{ appliedFilters.length }}
        </span>
        <div
          v-if="isFilterOpen"
          v-on-click-outside="filterOutsideClickHandler"
          class="absolute z-40 mt-2 ltr:right-0 rtl:left-0 top-full w-[calc(100vw-3rem)] max-w-3xl lg:w-[750px]"
        >
          <CommercialFilter
            :applied-filters="appliedFilters"
            :utm-values="utmValues"
            @apply="applyFilters"
          />
        </div>
      </div>
    </div>

    <ActiveFilterPreview
      v-if="appliedFilters.length"
      data-pdf-ignore
      :applied-filters="previewFilters"
      :clear-button-label="t('DASHBOARD.COMERCIAL.FILTER.CLEAR')"
      :more-filters-label="
        t('DASHBOARD.COMERCIAL.FILTER.MORE', {
          count: appliedFilters.length - 2,
        })
      "
      @clear-filters="applyFilters([])"
      @open-filter="isFilterOpen = true"
    />

    <p data-pdf-ignore class="text-xs text-n-slate-10">
      {{ t(`DASHBOARD.COMERCIAL.DATE_MODE.${dateMode.toUpperCase()}_HELP`) }}
    </p>

    <p v-if="!hasCompleteRange" class="text-sm text-n-slate-11">
      {{ t('DASHBOARD.ATENDIMENTO.PERIOD.CUSTOM_RANGE_INCOMPLETE') }}
    </p>
    <template v-else>
      <GoalsHero :params="baseParams" />
      <SummaryCards :params="baseParams" />
      <WidgetsSection v-if="isWidgetsEnabled" :params="baseParams" />
      <FunnelSection :params="baseParams" />
      <EvolutionSection :params="baseParams" />
      <SalesRepSection :params="baseParams" />
      <SourceSection :params="baseParams" />
      <LossReasonSection :params="baseParams" />
      <StalledSection :params="baseParams" />
    </template>
  </div>
</template>
